import React, { Component } from 'react';
import QRCode from 'qrcode.react';
import Modal from '../components/Modal';
import { getAuthHeaders } from '../../utils';

/**
 * Standalone-draft share modal. Talks to POST /api/v1/core/drafts/:draftId/share
 * which returns { shareableUrl, contributorCount }. Each visitor that opens the
 * link gets a fresh copy of this draft's title/content (no chapter required).
 *
 * Props:
 *  - isOpen   : boolean
 *  - onClose  : () => void
 *  - draftId  : string (uuid)
 */
class ShareDraftModal extends Component {
  state = {
    loading: false,
    shareableUrl: null,
    contributorCount: 0,
    copied: false,
    error: null,
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.generateShareLink();
    }
  }

  generateShareLink = async () => {
    const { draftId } = this.props;
    if (!draftId) {
      this.setState({ error: 'Missing draft id', loading: false });
      return;
    }

    this.setState({ loading: true, error: null, shareableUrl: null, contributorCount: 0 });

    try {
      const res = await fetch(`/api/v1/core/drafts/${draftId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to share draft (${res.status})`);
      }

      const data = await res.json();
      const fullUrl = `${window.location.origin}${data.shareableUrl}`;

      this.setState({
        loading: false,
        shareableUrl: fullUrl,
        contributorCount: Number(data.contributorCount ?? 0),
        error: null,
      });
    } catch (err) {
      this.setState({
        loading: false,
        error: err?.message || 'Failed to generate share link',
      });
    }
  };

  copyToClipboard = async () => {
    const { shareableUrl } = this.state;
    if (!shareableUrl) return;

    try {
      await navigator.clipboard.writeText(shareableUrl);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  handleClose = () => {
    this.setState({ copied: false });
    this.props.onClose?.();
  };

  render() {
    const { isOpen } = this.props;
    const { loading, shareableUrl, contributorCount, copied, error } = this.state;

    return (
      <Modal isOpen={isOpen} onClose={this.handleClose}>
        <div className="share-chapter-modal">
          <h2>Share Draft</h2>

          {loading && <p>Generating shareable link...</p>}

          {error && (
            <div className="share-chapter-modal__error">
              <p>{error}</p>
              <button
                type="button"
                className="btn primary"
                onClick={this.generateShareLink}
              >
                Retry
              </button>
            </div>
          )}

          {shareableUrl && !loading && (
            <>
              <div className="share-chapter-modal__link-row">
                <input
                  type="text"
                  value={shareableUrl}
                  readOnly
                  className="share-chapter-modal__link-input"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className="btn primary"
                  onClick={this.copyToClipboard}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              <div className="share-chapter-modal__qr">
                <QRCode
                  value={shareableUrl}
                  size={200}
                  level="H"
                  includeMargin
                />
              </div>

              <p className="share-chapter-modal__info">
                Anyone with this link gets their own copy of this draft to read,
                edit, and coach on.
              </p>

              <p className="share-chapter-modal__contributors">
                {contributorCount === 0
                  ? 'No one has opened this link yet.'
                  : contributorCount === 1
                  ? '1 contributor has opened this link.'
                  : `${contributorCount} contributors have opened this link.`}
              </p>
            </>
          )}
        </div>
      </Modal>
    );
  }
}

export default ShareDraftModal;
